import React from "react";
import { useLocation } from "react-router-dom";
import { useLiveData } from "../common/utils";
import styled from "styled-components";
import { Account, AccountContext } from "../common/context/AccountContext";
import { useLoadingIndicator } from "../common/context/LoadingIndicatorContext";
import { useTranslation } from "react-i18next";
import { Col, Row, Form, Button } from "react-bootstrap";
import { AppFlowActionButton, BackNav, FlexRow, PageContainer } from "../common/CommonStyles";
import { ApiContext } from "../common/context/ApiContext";
import {
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

const SubmitButton = styled(AppFlowActionButton)`
  font-weight: 400;
`;

export const FeedbackPage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { accountLiveData } = React.useContext(AccountContext);
  const [account, setAccount] = React.useState<Account>();
  const [feedback, setFeedback] = React.useState<string>("");
  const [isFeedbackResponseDialogOpen, setIsFeedbackResponseDialogOpen] =
    React.useState(false);
  const { startLoading, stopLoading } = useLoadingIndicator(false);
  const { sendFeedback } = React.useContext(ApiContext);
  const textAreaAnchorRef = React.useRef<HTMLTextAreaElement>(null);

  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const prefill = searchParams.get("prefill");
    if (prefill === "1") {
      setFeedback(t("send_feedback_request_for_content_prompt"));
    }
  }, [t, location, setFeedback]);

  async function getOSInfo() {
    const userAgentData = (navigator as any).userAgentData;
    if (userAgentData?.getHighEntropyValues) {
      try {
        const ua = await userAgentData.getHighEntropyValues([
          "platform",
          "platformVersion",
        ]);
        return `${ua.platform} ${ua.platformVersion}`;
      } catch (err) {
        // userAgentData not available
      }
    }
    const ua = navigator.userAgent;
    let platform = "Unknown";
    let version = "";

    if (/Mac OS X/.test(ua)) {
      platform = "macOS";
      version = ua.match(/Mac OS X ([0-9_]+)/)?.[1]?.replace(/_/g, ".") || "";
    } else if (/Windows NT/.test(ua)) {
      platform = "Windows";
      version = ua.match(/Windows NT ([0-9.]+)/)?.[1] || "";
    } else if (/Linux/.test(ua)) {
      platform = "Linux";
    }
    return `${platform} ${version}`;
  }

  async function getBrowserInfo() {
    const userAgentData = (navigator as any).userAgentData;
    if (userAgentData?.getHighEntropyValues) {
      try {
        const ua = await userAgentData.getHighEntropyValues(["uaFullVersion"]);
        const browser = userAgentData.brands?.[1]?.brand ?? "Unknown Browser";
        const browserVersion = ua.uaFullVersion ?? "";
        return `${browser} ${browserVersion}`;
      } catch (err) {
        // userAgentData not available
      }
    }
    return navigator.userAgent;
  }

  const onSendFeedback = React.useCallback(async () => {
    startLoading();
    const user = accountLiveData.value!!.user;
    const profile = accountLiveData.value.profile?.id || null;
    const data = {
      profile: user.has_given_verified_consent ? profile : null,
      device_model: user.has_given_verified_consent
        ? await getBrowserInfo()
        : "",
      device_os_version: user.has_given_verified_consent
        ? await getOSInfo()
        : "",
      app_version: "", // Build hash?
      app_build_type: user.has_given_verified_consent
        ? process.env.node_env === "production"
          ? "prod"
          : "dev"
        : "",
      user_feedback: feedback,
      device_logs: "",
    };
    await sendFeedback(data);
    setIsFeedbackResponseDialogOpen(true);
    setFeedback("");
    stopLoading();
  }, [startLoading, stopLoading, feedback, sendFeedback, accountLiveData]);

  useLiveData(
    React.useCallback(async (account: Account) => {
      if (account.hasUserAndMaybeProfile()) {
        setAccount(account);
      }
    }, []),
    accountLiveData
  );

  if (!account) {
    return <></>;
  }

  return (
    <PageContainer>
      <Row className="align-items-center">
        <FlexRow>
          <BackNav label={t("generic_nav_back")} />
        </FlexRow>
      </Row>
      <div className="text-center mt-4 mb-3">
        <h3>{t("send_feedback")}</h3>
      </div>
      <Row className="justify-content-center mt-4">
        <Col xs={12} lg={8} xl={6}>
          <Form>
            <Form.Group as={Row} className="mb-3" controlId="firstName">
              <Col>
                <Form.Control
                  as="textarea"
                  ref={textAreaAnchorRef}
                  placeholder={t("feedback_placeholder")}
                  value={feedback}
                  onChange={(e) => {
                    setFeedback(e.target.value);
                  }}
                />
              </Col>
            </Form.Group>
          </Form>
          <Row className="mt-4 mb-2 justify-content-center">
            <Col xs={6} md={4} lg={3}>
              <SubmitButton
                disabled={!feedback.trim()}
                isEnabled={!!feedback.trim()}
                onClick={() => {
                  onSendFeedback();
                }}
              >
                {t("generic_send")}
              </SubmitButton>
            </Col>
          </Row>
        </Col>
      </Row>
      <Dialog
        open={isFeedbackResponseDialogOpen}
        onClose={() => setIsFeedbackResponseDialogOpen(false)}
        aria-labelledby="feedback-response-dialog-title"
        aria-describedby="feedback-response-dialog-description"
      >
        <DialogContent
          sx={{
            minWidth: { xs: "auto", sm: 400 },
            maxWidth: "90vw",
          }}
        >
          <DialogContentText id="feedback-response-dialog-description">
            {t("feedback_sent")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={(e) => {
              e.preventDefault();
              setIsFeedbackResponseDialogOpen(false);
            }}
          >
            {t("generic_ok")}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};
